import { FormDatePicker, FormInputGroup, FormText } from 'components/form';
import { FormModal } from 'components/form/FormModal/FormModal';
import { endOfDay, startOfDay } from 'date-fns';
import { useAuth } from 'hooks';
import { FieldValues, useFieldArray } from 'react-hook-form';
import { useLazyResolveDeliveryTicketQuery } from 'store/services/planning';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import * as yup from 'yup';
import styles from './ResolveDeliveryModal.module.scss';
import { DeviceLookup } from 'views/Planning/Planning';
import { useEffect, useState } from 'react';
import { DeviceFillupType } from 'types';
import { useDispatch } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { Alert, IconButton } from '@mui/material';
import { Delete, Info } from '@mui/icons-material';

type DeliveryInputType = DeviceFillupType & {
    name?: string;
    customerName?: string;
    ticketId?: string;
    deviceId?: string;
    disabled?: boolean;
    activeTicketId?: string; // used for bulk fillup modal on devices page
};

type DeliveryResolutionInput = {
    deliveries: DeliveryInputType[];
};

export const defaultDeliveryValues: DeliveryResolutionInput = {
    deliveries: Array(3).fill({
        amount: 0,
        time: new Date(),
    }),
};

const deliveriesSchema = yup
    .object<{ [k in keyof DeliveryResolutionInput]: yup.AnySchema }>({
        deliveries: yup
            .array()
            .of(
                yup.object<{ [k in keyof DeviceFillupType]: yup.AnySchema }>({
                    amount: yup
                        .number()
                        .moreThan(
                            0,
                            'Must select a non-negative gallon amount.'
                        )
                        .required(),
                    time: yup
                        .date()
                        .max(
                            endOfDay(new Date()),
                            'Must select a present or past date.'
                        )
                        .required(),
                })
            )
            .min(1)
            .required(),
    })
    .required();

interface ResolveDeliveryModalProps {
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    ticketsToResolve: (Ticket | undefined)[];
    resetTickets: (t: Ticket[]) => void;
    devicesList: DeviceLookup;
}

export const ResolveDeliveryModal = ({
    modalOpen,
    setModalOpen,
    ticketsToResolve,
    resetTickets,
    devicesList,
}: ResolveDeliveryModalProps) => {
    const { user } = useAuth();
    const [resolveDeliveryQuery] = useLazyResolveDeliveryTicketQuery();
    const [deliveryDefaults, setDeliveryDefaults] =
        useState<DeliveryResolutionInput>({ deliveries: [] });

    const dispatch = useDispatch();

    useEffect(() => {
        const defaults = ticketsToResolve.map((ticket, index) => {
            const deviceName = ticket?.deviceId
                ? devicesList[ticket.deviceId]?.name ??
                  devicesList[ticket.deviceId]?.shortDeviceId
                : `Ticket #${index + 1}`;

            const customerName =
                ticket?.deviceId && devicesList[ticket.deviceId]?.customer_name;

            return {
                amount: ticket?.plannedGallons ?? 0,
                time: ticket?.plannedDate
                    ? new Date(ticket.plannedDate)
                    : new Date(),
                name: deviceName,
                customerName,
                ticketId: ticket?.id,
                disabled: ticket?.ticketDirectory === 'resolved',
            };
        });

        setDeliveryDefaults({
            deliveries: defaults,
        });
    }, [ticketsToResolve, devicesList]);

    const onSubmit = (data: FieldValues) => {
        const responses = data.deliveries.reduce(
            (res: any, delivery: DeliveryInputType) => {
                if (
                    delivery &&
                    delivery?.ticketId &&
                    delivery?.time &&
                    delivery?.amount
                ) {
                    return {
                        ...res,
                        [delivery.ticketId]: {
                            dateDelivered: startOfDay(delivery.time),
                            gallonsDelivered: Number(delivery.amount),
                        },
                    };
                }

                return res;
            },
            {}
        );

        const failedUpdates: string[] = [];

        Promise.all(
            ticketsToResolve
                .map((ticket) => {
                    if (!ticket) {
                        return null;
                    }

                    const device = devicesList[ticket.deviceId];

                    const newTicket = {
                        ...ticket,
                        ...responses[ticket.id],
                        tankCapacity: device.capacity,
                        status: 'done',
                    };

                    if (user?.protected?.supplierId) {
                        return resolveDeliveryQuery({
                            newTicket,
                            supplierId: user.protected.supplierId,
                            deviceId: newTicket.deviceId,
                        })
                            .then((result) => {
                                if (result?.data) {
                                    return result.data;
                                }
                            })
                            .catch(() => {
                                if (ticket) {
                                    failedUpdates.push(
                                        ticket.name ??
                                            devicesList[ticket.deviceId].name ??
                                            devicesList[ticket.deviceId]
                                                .shortDeviceId
                                    );
                                }
                            });
                    }

                    return newTicket;
                })
                .filter((item: any) => !!item)
        ).then((updatedTickets) => {
            resetTickets(updatedTickets);
            if (!failedUpdates.length) {
                dispatch(
                    activateSnackbar({
                        message: 'Tickets successfully resolved',
                        severity: 'success',
                    })
                );
            }
        });
    };

    return (
        <FormModal
            defaultValues={deliveryDefaults}
            validationSchema={deliveriesSchema}
            title="Resolve Ticket"
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onValidHandler={onSubmit}
            maxWidth="sm"
            onClose={() => resetTickets([])}
            testId="ResolveDeliveryModal"
        >
            <ResolutionForm />
        </FormModal>
    );
};

type ResolutionFormProps = {
    onRemove?: (id: string) => void;
};

export const ResolutionForm = ({ onRemove }: ResolutionFormProps) => {
    const { fields } = useFieldArray<DeliveryResolutionInput>({
        name: 'deliveries',
    });

    return (
        <div className={styles.resolutionForm}>
            {fields.map((field, index) => {
                return (
                    <div key={field.id}>
                        <FormInputGroup>
                            <b className={styles.deviceName}>
                                {field.customerName
                                    ? `${field.customerName}: ${field.name}`
                                    : field.name}
                            </b>
                            <FormText
                                field={`deliveries.${index}.amount`}
                                label="Gallons Delivered"
                                isNumber
                                disabled={field?.disabled}
                            />
                            <FormDatePicker
                                field={`deliveries.${index}.time`}
                                label="Date Delivered"
                                disabled={field?.disabled}
                            />
                            {onRemove && (
                                <IconButton
                                    className={styles.removeFieldButton}
                                    onClick={() => {
                                        field.deviceId &&
                                            onRemove(field.deviceId);
                                    }}
                                    size="small"
                                >
                                    <Delete />
                                </IconButton>
                            )}
                        </FormInputGroup>
                        {field.activeTicketId && (
                            <Alert
                                severity="warning"
                                icon={<Info />}
                                className={styles.activeAlert}
                                data-testid="DeviceHasActiveTicket"
                            >
                                Monitor <b>{field?.name ?? ''}</b> has an active
                                delivery. Did you mean to resolve that ticket?
                                If so, you may do so in the planning view
                            </Alert>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
